import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { deleteUserNotification, fetchUserNotifications } from "../api/notifications";
import { restoreAuthFromStorage, registerOnLogout } from "../api/Client";

export type Notification = {
  id: string;
  title: string;
  description: string;
  date: string;
  read: boolean;
  deletedAt?: string | null;
  source?: "remote" | "local";
};

export type AddLocalNotificationInput = {
  id: string;
  title: string;
  description: string;
  date?: string;
  read?: boolean;
};

type NotificationsContextValue = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: string) => void;
  refresh: () => Promise<void>;
  addLocalNotification: (input: AddLocalNotificationInput) => void;
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

function formatDateTime(dateStr?: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readIdsRef = useRef<Set<string>>(new Set());
  const deletedCacheRef = useRef<Map<string, Notification>>(new Map());
  const localExtrasRef = useRef<Notification[]>([]);

  const DAY_MS = 24 * 60 * 60 * 1000;

  const cacheKey = useCallback(
    (id: number) => `notifications_deleted_${id}`,
    []
  );

  const loadDeletedCache = useCallback(
    (uid: number) => {
      try {
        const raw = localStorage.getItem(cacheKey(uid));
        const arr: Notification[] = raw ? JSON.parse(raw) : [];
        const fresh = arr.filter(
          (n) =>
            !n.deletedAt ||
            Date.now() - new Date(n.deletedAt).getTime() < DAY_MS
        );
        deletedCacheRef.current = new Map(fresh.map((n) => [n.id, n]));
      } catch (err) {
        console.warn("No se pudo leer caché de notificaciones:", err);
        deletedCacheRef.current = new Map();
      }
    },
    [cacheKey]
  );

  const persistDeletedCache = useCallback(
    (uid: number) => {
      if (!uid) return;
      try {
        const arr = Array.from(deletedCacheRef.current.values());
        localStorage.setItem(cacheKey(uid), JSON.stringify(arr));
      } catch (err) {
        console.warn("No se pudo guardar caché de notificaciones:", err);
      }
    },
    [cacheKey]
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { user } = await restoreAuthFromStorage();
        if (mounted && user?.id) {
          setUserId(Number(user.id));
          loadDeletedCache(Number(user.id));
        }
      } catch (err) {
        console.warn("No se pudo restaurar el usuario para notificaciones:", err);
      }
    })();
    registerOnLogout(() => {
      setUserId(null);
      setNotifications([]);
      readIdsRef.current = new Set();
      deletedCacheRef.current = new Map();
      localExtrasRef.current = [];
    });
    return () => {
      mounted = false;
    };
  }, [loadDeletedCache]);

  const mergeWithLocal = useCallback(
    (base: Notification[]) => {
      const ids = new Set(base.map((n) => n.id));
      const extras = localExtrasRef.current;
      const extrasToAdd = extras.filter((n) => !ids.has(n.id));
      return [...base, ...extrasToAdd];
    },
    []
  );

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUserNotifications(userId);
      loadDeletedCache(userId);
      const cachedDeleted = Array.from(deletedCacheRef.current.values());
      const mapped = data.map((n) => {
        const id = String(n.notification_id ?? n.user_id ?? Math.random());
        const cached = deletedCacheRef.current.get(id);
        return {
          id,
          title: n.title || "Sin título",
          description: n.message || "",
          date: formatDateTime(n.created_at),
          read: readIdsRef.current.has(id) || !!cached,
          deletedAt: cached?.deletedAt,
          source: "remote",
        };
      });
      const mergedIds = new Set(mapped.map((m) => m.id));
      const merged = [...mapped, ...cachedDeleted.filter((c) => !mergedIds.has(c.id))];
      const withLocal = mergeWithLocal(merged);
      setNotifications(withLocal);
      persistDeletedCache(userId);
    } catch (err: any) {
      console.error("❌ Error cargando notificaciones:", err);
      setError(err?.response?.data?.error || "No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, [userId, loadDeletedCache, persistDeletedCache, mergeWithLocal]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markAsRead = useCallback((id: string) => {
    const nowIso = new Date().toISOString();
    if (userId) {
      const target = notifications.find((n) => n.id === id);
      if (target) {
        deletedCacheRef.current.set(id, { ...target, deletedAt: nowIso, read: true });
        persistDeletedCache(userId);
        const numericId = Number(id);
        if (!Number.isNaN(numericId)) {
          deleteUserNotification(userId, numericId).catch((err) =>
            console.error("❌ Error marcando como leída (delete):", err)
          );
        }
      }
    }
    readIdsRef.current.add(id);
    localExtrasRef.current = localExtrasRef.current.map((notification) =>
      notification.id === id ? { ...notification, read: true, deletedAt: nowIso } : notification
    );
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true, deletedAt: nowIso } : notification
      )
    );
  }, [notifications, persistDeletedCache, userId]);

  const addLocalNotification = useCallback(
    (input: AddLocalNotificationInput) => {
      const mappedDate = formatDateTime(input.date || new Date().toISOString());
      const newNotification: Notification = {
        id: input.id,
        title: input.title || "Notificación",
        description: input.description || "",
        date: mappedDate,
        read: !!input.read,
        source: "local",
      };
      localExtrasRef.current = [
        ...localExtrasRef.current.filter((n) => n.id !== newNotification.id),
        newNotification,
      ];
      setNotifications((prev) => {
        const base = prev.filter(
          (n) => !localExtrasRef.current.some((extra) => extra.id === n.id)
        );
        return mergeWithLocal(base);
      });
    },
    [mergeWithLocal]
  );

  const visibleNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => !n.deletedAt || Date.now() - new Date(n.deletedAt).getTime() < DAY_MS
      ),
    [notifications]
  );

  const value = useMemo(
    () => ({
      notifications: visibleNotifications,
      unreadCount: visibleNotifications.filter((n) => !n.read).length,
      loading,
      error,
      markAsRead,
      refresh,
      addLocalNotification,
    }),
    [visibleNotifications, loading, error, markAsRead, refresh, addLocalNotification]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within a NotificationsProvider");
  return ctx;
}
